/** /event_delivery_logs/ — ops-wide event delivery log search across tenants.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array — identical contract to `/bookings/`, but
 * over the immutable Event_Delivery_Log table (see EventDeliveryLogFilterNode
 * in the OpenAPI spec). Rows have UUID (string) ids.
 *
 * Tenant suggestions reuse the shared Bookings autocomplete endpoints via
 * `makeSuggestionFetcher`; event-code suggestions have their own static-catalog
 * endpoint (`fetchEventSuggestions`).
 */
import type { AiDiagStartBody, DiagnoseLaunchContext } from '@/features/ai-diag/types';
import type { SuggestionFetcher, WireNode } from '@/components/QuerySearch';

import { apiGet, apiPost, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface DeliveryLogListItem {
  /** UUIDv7 — time-ordered. */
  id: string;
  /** Emitted at (the partition key). */
  created_ts: string;
  finalized_ts: string | null;
  expires_ts: string | null;
  duration_ms: number;
  status: number;
  status_display: string;
  /** Event log id — shared across redeliveries of the same event. */
  log_id: string;
  /** Free-text tag; redelivery stamps its run tag here. */
  log_tag: string | null;
  event: string;
  rendering_lang: string | null;
  previous_eda_uuid: string | null;
  /** On an orphaned row (status 10): the delivery that re-dispatched what this
   *  one never attempted. */
  recovery_uuid: string | null;
  /** On a recovery row: the orphaned delivery it was created to finish. */
  recovered_from_uuid: string | null;
  tour_operator_id: number | null;
  tour_operator_code: string | null;
  /** The row's tenant's `Tour_Operator.kind` (list rows only) — the Tenant cell marks a
   *  sandbox. */
  tour_operator_environment?: TourOperatorEnvironment | null;
  user_id: number | null;
  user_email: string | null;
  tour_id: number | null;
  participant_id: number | null;
  trip_start_ts: string | null;
  departure: string | null;
  departure_id: number | null;
  order_id: number | null;
  order_ref: string | null;
  booking_id: string | null;
  supplier_ref: string | null;
  reseller_ref: string | null;
  request_id: string | null;
  error_series_id: string | null;
  n_notifications: number;
  n_success: number;
  n_error: number;
  /** Delivery methods a sandbox captured (kept in its outbox, nothing sent); 0 on every
   *  production row. Served on list rows, the detail and its chain items; absent reads as 0. */
  n_captured?: number;
  archive_s3_key: string;
}

/** One per-notification × delivery-method result row. `delivery_method` uses
 *  the CANONICAL codes (0=Email 1=SMS 2=Chat 3=Push 4=Sentry 5=SNS 6=HTTP
 *  7=WhatsApp 8=Airbnb); the server maps `delivery_method_name` from them. */
export interface DeliveryLogNotification {
  id: number;
  created_ts: string;
  notification_id: number;
  notification_name: string;
  delivery_method: number;
  delivery_method_name: string;
  status: number;
  status_display: string;
  n_success: number;
  n_error: number;
  error_series_id: string | null;
}

/** Brief row in the "other deliveries sharing this log_id" chain. */
export interface DeliveryLogChainItem {
  id: string;
  created_ts: string;
  log_tag: string | null;
  status: number;
  status_display: string;
  error_series_id: string | null;
  n_notifications: number;
  n_success: number;
  n_error: number;
  recovery_uuid: string | null;
  recovered_from_uuid: string | null;
}

export interface DeliveryLogDetail extends DeliveryLogListItem {
  twilio_conversation_id: number | null;
  /** Flexible ad-hoc tags (JSONB). */
  tags: Record<string, unknown> | null;
  archive_s3_bucket: string | null;
  notifications: DeliveryLogNotification[];
  /** Other deliveries sharing this log_id, newest first (max 50). */
  chain: DeliveryLogChainItem[];
}

/** GET /{id}/archive/ — every S3-archive section the Django admin shows.
 *  `available: false` (with HTTP 200) means no archive / expired object. */
export interface DeliveryLogArchive {
  available: boolean;
  can_redeliver?: boolean;
  /** True when the row is still ORPHANED and the manifest can rebuild the
   *  event — i.e. `recover()` would be accepted. */
  can_recover?: boolean;
  error?: string;
  manifest?: Record<string, unknown> | null;
  delivery?: string[] | null;
  delivery_decisions?: unknown;
  rendering_context?: Record<string, unknown> | null;
  rendered_blocks?: Record<string, unknown> | null;
  composition?: Record<string, unknown> | null;
  notifications?: Record<string, unknown> | null;
  s3?: { bucket: string; key: string; console_url: string };
}

/** The list endpoint's paginated envelope carries a capped-count flag. */
export type DeliveryLogListResponse = PaginatedResponse<DeliveryLogListItem> & {
  /** Present in `count_mode=sync` only: the sync count is capped at 10 000
   *  probed rows. The async/deferred on-demand total is exact and uncapped, so
   *  there is nothing to cap and the key is absent. */
  count_is_capped?: boolean;
};

export interface DeliveryLogListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default (`deferred` on this grid) — that omission is what keeps a cold
   *  first load free of a COUNT. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

function toQuery(p: DeliveryLogListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  if (p.count_mode) usp.set('count_mode', p.count_mode);
  if (p.count_only) usp.set('count_only', '1');
  return usp.toString();
}

export async function list(p: DeliveryLogListParams = {}): Promise<DeliveryLogListResponse> {
  const qs = toQuery(p);
  return apiGet(`/event_delivery_logs/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** Total only, for the grid's on-demand row count. `async` so the count runs
 *  in Celery: an exact, uncapped total that never occupies a web worker. */
export async function count(p: DeliveryLogListParams = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...p, count_only: true, count_mode: 'async' });
  return apiGet(`/event_delivery_logs/?${qs}`);
}

/** GET /event_delivery_logs/{id}/ — full row + notification results + chain. */
export async function retrieve(id: string): Promise<DeliveryLogDetail> {
  return apiGet(`/event_delivery_logs/${id}/`);
}

/** GET /event_delivery_logs/{id}/archive/ — lazy-loaded by the Delivery/Debug tabs. */
export async function archive(id: string): Promise<DeliveryLogArchive> {
  return apiGet(`/event_delivery_logs/${id}/archive/`);
}

/** POST /event_delivery_logs/{id}/recover/ — recover one orphaned delivery.
 *
 *  Queues the same work the recovery cron does: re-dispatch only the
 *  notifications (and delivery methods within them) the crashed delivery never
 *  attempted, replaying its archived context and matching decisions. 202 means
 *  queued — the outcome lands on the row (status 9 → 10/11/12, `recovery_uuid`,
 *  and `tags.recovery`), so refetch rather than expecting a result here.
 *  `force` recovers an orphan older than the server's max-age guard. */
export async function recover(id: string, force = false): Promise<{ queued: boolean }> {
  return apiPost(`/event_delivery_logs/${id}/recover/`, { force });
}

/** Event-code suggestions from the static event catalog (no DB behind it). */
export const fetchEventSuggestions: SuggestionFetcher = async (query, _extra, signal) => {
  const usp = new URLSearchParams();
  if (query) usp.set('q', query);
  const res = await apiGet<{ results: { code: string }[] }>(
    `/event_delivery_logs/autocomplete/events/?${usp.toString()}`,
    { signal },
  );
  return res.results.map((r) => ({ value: r.code, label: r.code }));
};

// ---- AI diagnostics (perm: console_event_delivery_ai) ----------------------
// Types are shared with future AI debugging tools — see features/ai-diag.

export type {
  AiDiagStartBody,
  DiagnoseLaunchContext,
  DiagnoseModelOption,
  DiagnoseResult,
} from '@/features/ai-diag/types';

export async function diagnoseLaunchContext(): Promise<DiagnoseLaunchContext> {
  return apiGet('/event_delivery_logs/diagnose/launch_context/');
}

/** POST /event_delivery_logs/{id}/diagnose/ — start an AI diagnostic run
 *  (or a follow-up when `thread_task_id` is set — the run reuses the
 *  thread's captured context plus the prior Q&A). 202 { task_id } — poll
 *  bgtasks; 409 carries the already-running task_id (adopt it). */
export async function startDiagnose(
  id: string,
  body: AiDiagStartBody,
): Promise<{ task_id: string; queued: boolean }> {
  return apiPost(`/event_delivery_logs/${id}/diagnose/`, body);
}

// Tenant suggestions are the SAME shared Bookings autocomplete endpoint;
// re-export the fetcher factory so deliveryLogsNamespaces has one import.
export { makeSuggestionFetcher } from './bookings';
